// Library Methods
import { useCallback, useMemo } from "react";

// Components
import ChartCross from "src/assets/shapes/ChartCross/ChartCross";
import ColoredCircle from "src/assets/shapes/ColoredCircle/ColoredCircle";
import Dash from "src/assets/shapes/Dash/Dash";
import Triangle from "src/assets/shapes/Triangle/Triangle";
import Skeleton from "@mui/material/Skeleton";

// Constants
import { eyeTypeEnums, gridTypeEnums } from "src/constants/enums";

// Types
import { ChartSpotType } from "src/services/exams/types";

// Utils
import {
  possibleChartPoints24Left,
  possibleChartPoints24Right,
  possibleChartPoints30,
} from "src/utils/exams/numberChartHelper";

const DEFAULT_CONTAINER_SIZE = 312;

type NumberGraphType = {
  points: Array<ChartSpotType>;
  eyeType: string;
  gridType: string | undefined;
  displayTriangle?: boolean;
  containerSize?: number;
  isLiveUpdate?: boolean;
};

const NumberGraph = ({
  points,
  eyeType,
  gridType,
  displayTriangle,
  containerSize = DEFAULT_CONTAINER_SIZE,
  isLiveUpdate = false,
}: NumberGraphType) => {
  const { LEFT, RIGHT } = eyeTypeEnums;

  const size = useMemo(() => containerSize * 0.9, [containerSize]);
  const tickSize = useMemo(() => size / 104, [size]);
  const scaleFactor = useMemo(() => size / DEFAULT_CONTAINER_SIZE, [size]);

  const borderWidth = useMemo(() => 2, []);
  const halfHeightPx = `${(size - borderWidth) / 2}px`;

  const is24C = useMemo(
    () => gridType === gridTypeEnums.G_24_2_C.type,
    [gridType]
  );

  const allExamPoints = useMemo(
    () =>
      gridType === gridTypeEnums.G_30_2.type
        ? possibleChartPoints30
        : eyeType === LEFT
          ? possibleChartPoints24Left
          : possibleChartPoints24Right,
    [gridType, eyeType, LEFT]
  );

  const calculateXPaddingNumberGraph = useCallback((xFloor: number) => {
    // jumpAxis lets us jump from negative x axis to positive x axis (half of total width + line width + padding)
    const jumpAxis = 133;

    if (xFloor <= 0) {
      return 160 - xFloor * -31;
    } else {
      return xFloor * 31 + jumpAxis;
    }
  }, []);

  const calculateYPaddingNumberGraph = useCallback((yFloor: number) => {
    // jumpAxis lets us jump from negative y axis to negative y axis (half of total width + line width + padding)
    const jumpAxis = 135;

    if (yFloor >= 0) {
      return 160 - yFloor * 29;
    } else {
      return jumpAxis + yFloor * -29;
    }
  }, []);

  const getPaddingLeft = useCallback(
    (spot: { x: number; y: number }, ml: number) => {
      let value = ml;

      if (is24C && 0 < spot.x && spot.x < 1) {
        value = ml + 6;
      } else if (is24C && eyeType === LEFT && spot.x === -1 && spot.y === -2) {
        value = ml - 10;
      } else if (is24C && eyeType === RIGHT && spot.x === 1 && spot.y === -2) {
        value = ml + 10;
      }

      return value * scaleFactor;
    },
    [is24C, LEFT, RIGHT, eyeType, scaleFactor]
  );

  const getPaddingTop = useCallback(
    (spot: { x: number; y: number }, mt: number) => {
      let value = mt;

      if (is24C && !Number.isInteger(spot.x) && spot.y === -2) {
        value = mt + 2;
      } else if (is24C && spot.y === -1.33) {
        value = mt + 6;
      }

      return value * scaleFactor;
    },
    [is24C, scaleFactor]
  );

  const getFontSize = useCallback(
    (spot: { x: number; y: number }) => {
      if (is24C && (!Number.isInteger(spot.x) || !Number.isInteger(spot.y))) {
        return "9px";
      }

      return "12px";
    },
    [is24C]
  );

  // Show loading skeleton if data is not yet processed
  if (points.length === 0) {
    return (
      <Skeleton
        variant="rounded"
        width="100%"
        height={DEFAULT_CONTAINER_SIZE}
        sx={{
          borderRadius: "4px",
        }}
      />
    );
  }

  return (
    <main
      style={{
        height: `${size}px`,
        width: `${size}px`,
        position: "relative",
      }}
    >
      <ChartCross
        gridType={gridType}
        width={size}
        height={size}
        tickSize={tickSize}
      />

      {/* Triangle to show the blind spot (don't show on 10-2 grid) */}
      {displayTriangle && (
        <div
          style={{
            position: "absolute",
            marginTop: `calc(${halfHeightPx} - 31px)`,
            // For left eyes, place the triangle on the left side of the grid. For right eyes, it's the opposite.
            marginLeft:
              eyeType === LEFT
                ? `calc(${(size * 1) / 6}px + ${(size / DEFAULT_CONTAINER_SIZE) * 30}px)`
                : `calc(${(size * 5) / 6}px - ${(size / DEFAULT_CONTAINER_SIZE) * 30}px)`,
          }}
        >
          <Triangle />
        </div>
      )}

      {/* Draw a dash for points that don't have a value in the array */}
      {allExamPoints.map((spot, i) => {
        // check if the current point has a valid decibelValue
        const hasValue = points
          .filter((point) => point.decibelValue !== null)
          .find((point) => point.x === spot.x && point.y === spot.y);

        const ml = calculateXPaddingNumberGraph(spot.x);
        const mt = calculateYPaddingNumberGraph(spot.y);

        if (hasValue) return null;

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              paddingLeft: getPaddingLeft(spot, ml),
              paddingTop: getPaddingTop(spot, mt),
            }}
          >
            <Dash size={8} />
          </div>
        );
      })}

      {/* In progress spots (live update modal) */}
      {/* Display an orange circle by default or a green circle on the "current spot" */}
      {points
        .filter((spot) => !spot.completed && spot.decibelValue !== null) // filter uncompleted spots with a valid decibelValue
        .map((spot, i) => {
          const ml = calculateXPaddingNumberGraph(spot.x);
          const mt = calculateYPaddingNumberGraph(spot.y);

          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: getPaddingLeft(spot, ml),
                paddingTop: getPaddingTop(spot, mt),
              }}
            >
              {!isLiveUpdate ? (
                <Dash size={8} />
              ) : (
                <ColoredCircle size={8} isOrange={!spot.current} />
              )}
            </div>
          );
        })}

      {/* Completed spots */}
      {points
        .filter((spot) => spot.completed)
        .map((spot, i) => {
          const decibelValue = spot.decibelValue;
          const ml = calculateXPaddingNumberGraph(spot.x);
          const mt = calculateYPaddingNumberGraph(spot.y);

          return (
            <div
              key={i}
              style={{
                position: "absolute",
                marginTop: "0px",
                paddingLeft: getPaddingLeft(spot, ml),
                paddingTop: getPaddingTop(spot, mt),
                fontSize: getFontSize(spot),
              }}
            >
              {Math.floor(decibelValue) >= 0 ? Math.floor(decibelValue) : "<0"}
            </div>
          );
        })}
    </main>
  );
};

export default NumberGraph;
